/**
 * OfferSendModal — Jun-2026 (User-Wunsch).
 * Versendet ein repräsentatives Fahrzeug-Angebot (gebrandetes PDF mit QR,
 * Verkäufer-Kontakt & Standort) an einen Kunden per E-Mail oder WhatsApp.
 * Wird aus der Disposition-Fahrzeugliste geöffnet.
 */
import { useEffect, useMemo, useState } from "react";
import { Mail, MessageCircle, FileText, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

export default function OfferSendModal({ item, onClose }) {
    const title = item.title || `${item.brand || ""} ${item.model || ""}`.trim() || "Fahrzeug";
    const listingUrl = `${window.location.origin}/listing/${item.id}`;
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/listings/${item.id}/offer.pdf`;

    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [viaWhatsapp, setViaWhatsapp] = useState(false);
    const [ccSelf, setCcSelf] = useState(true);
    const [followup, setFollowup] = useState(true);
    const [followupDays, setFollowupDays] = useState(3);
    const [recentRecipients, setRecentRecipients] = useState([]);
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState(
        `Guten Tag,\n\nanbei unser Angebot zum Fahrzeug „${title}".\nAlle Details & Fotos finden Sie hier: ${listingUrl}\n\nFür Rückfragen stehe ich Ihnen gerne zur Verfügung.\n\nMit freundlichen Grüßen`
    );

    // Jun-2026 — zuletzt verwendete Empfänger als Schnellauswahl laden.
    useEffect(() => {
        let active = true;
        api.get("/me/offer-recipients")
            .then((r) => { if (active) setRecentRecipients(r.data?.recipients || []); })
            .catch(() => { /* optional */ });
        return () => { active = false; };
    }, []);

    const waLink = useMemo(() => {
        const digits = (phone || "").replace(/[^0-9]/g, "");
        return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
    }, [phone, message]);

    const sendEmail = async () => {
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
            toast.error("Bitte eine gültige E-Mail-Adresse angeben"); return;
        }
        setSending(true);
        try {
            const { data } = await api.post(`/listings/${item.id}/offer/email`, {
                recipient_email: email.trim(), message: message.trim() || null, cc_self: ccSelf,
                create_followup: followup, followup_days: followupDays,
            });
            toast.success(`Angebot an ${data.recipient}${data.cc_self ? " (Kopie an Sie)" : ""} gesendet${data.mocked ? " (Versand simuliert – kein Mail-Key)" : ""}.${followup ? ` Wiedervorlage in ${followupDays} Tagen angelegt.` : ""}`);
            if (!viaWhatsapp) onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
        } finally { setSending(false); }
    };

    const sendWhatsapp = () => {
        const digits = (phone || "").replace(/[^0-9]/g, "");
        if (digits.length < 6) { toast.error("Bitte eine gültige Handynummer (inkl. Ländervorwahl) angeben"); return; }
        window.open(waLink, "_blank", "noopener");
        toast.success("WhatsApp wird geöffnet – bitte Nachricht dort absenden.");
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="offer-send-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-blue-700" /> Angebot erstellen &amp; senden</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="text-sm font-bold text-[#111827]">{title}</div>
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 hover:underline" data-testid="offer-open-pdf">
                        <FileText className="w-4 h-4" /> Angebots-PDF (Vorschau)
                    </a>

                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">E-Mail des Empfängers</Label>
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kunde@example.de" data-testid="offer-email-input" className="rounded-sm" />
                        {recentRecipients.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="offer-recent-recipients">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 self-center">Zuletzt:</span>
                                {recentRecipients.map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        onClick={() => setEmail(r)}
                                        className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${email === r ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"}`}
                                        data-testid={`offer-recipient-chip-${r}`}
                                    >
                                        {r}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">Handynummer (für WhatsApp, inkl. Ländervorwahl)</Label>
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="z. B. 491701234567" data-testid="offer-phone-input" className="rounded-sm" />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-800 cursor-pointer">
                        <input type="checkbox" checked={viaWhatsapp} onChange={(e) => setViaWhatsapp(e.target.checked)} data-testid="offer-whatsapp-check" />
                        Auch per WhatsApp erreichbar / senden
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-800 cursor-pointer">
                        <input type="checkbox" checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} data-testid="offer-cc-check" />
                        Kopie der E-Mail an mich
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-800 cursor-pointer">
                        <input type="checkbox" checked={followup} onChange={(e) => setFollowup(e.target.checked)} data-testid="offer-followup-check" />
                        <Clock className="w-3.5 h-3.5 text-gray-500" /> Wiedervorlage anlegen („Nachfassen")
                    </label>
                    {followup && (
                        <div className="flex items-center gap-1.5 pl-6" data-testid="offer-followup-days">
                            <span className="text-[11px] text-gray-500 font-bold uppercase tracking-wider mr-1">Frist:</span>
                            {[3, 7, 14].map((d) => (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => setFollowupDays(d)}
                                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${followupDays === d ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"}`}
                                    data-testid={`offer-followup-days-${d}`}
                                >
                                    {d} Tage
                                </button>
                            ))}
                        </div>
                    )}
                    <div>
                        <Label className="text-xs font-bold uppercase tracking-wider">Nachricht (Link &amp; QR sind im PDF enthalten)</Label>
                        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} data-testid="offer-message-input" className="rounded-sm text-sm" />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    {viaWhatsapp && (
                        <Button onClick={sendWhatsapp} className="bg-green-600 hover:bg-green-700 text-white" data-testid="offer-send-whatsapp">
                            <MessageCircle className="w-4 h-4 mr-1" /> Per WhatsApp
                        </Button>
                    )}
                    <Button onClick={sendEmail} disabled={sending} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="offer-send-email">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Per E-Mail senden</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
